/**
 * InterestedFeed - Main Recruiter Candidate Search Page
 * Route: /candidate-search
 *
 * Full-width search + filters, discover candidates, saved toggle.
 * "Interested" candidates have their own dedicated page at /interested (InterestedPage.tsx).
 *
 * NOT to be confused with:
 *   - CVSearch.tsx (/search) - old simple text search, deprecated
 *   - CandidateSearch.tsx (/candidate-search-legacy) - old JD upload + filter search, deprecated
 */
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Building2, Loader2, Users, ChevronRight, ChevronLeft, Search, MessageSquare, X, Send, ArrowLeft, ArrowRight, AlertCircle, Bookmark, EyeOff, Briefcase, MapPin, Target, Globe, Star, ChevronDown, RefreshCw, Filter, Zap, GripVertical, Sparkles, FileText, FolderOpen, LayoutGrid } from 'lucide-react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useUserProfile } from '../contexts/UserProfileContext';
import { showToast, handleApiError, RateLimitInfo } from '../utils/errorHandler';
import RateLimitNotification from '../components/RateLimitNotification';
import {
  RecruiterGateCard,
  RecruiterInlineEmpty,
  RecruiterLoadingScreen,
  RecruiterPageShell,
} from '../components/Recruiter/RecruiterStates';
import RecruiterLoggedOutGate from '../components/RecruiterLoggedOutGate';
import AtsPushButton from '../components/AtsPushButton';
import PassportViewer from '../components/Passport/PassportViewer';
import { readRecruiterLangFilterTab, persistRecruiterLangFilterTab } from '../constants/recruiterLanguageFilter';
import {
  RECRUITER_LOCATION_SUGGESTIONS, RECRUITER_ROLE_SUGGESTIONS,
  RECRUITER_SKILLS_SUGGESTIONS, RECRUITER_LEVEL_OPTIONS, parseRoleLevel,
} from '../constants/recruiterFilterOptions';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

function shuffleAndPick<T>(arr: T[], n: number): T[] {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, n);
}

interface QuickFilterState {
  location: string[];
  role: string[];
  level: string[];
  skills: string[];
}

const QUICK_FILTER_STORAGE_KEY = 'cevee_recruiter_quick_filters';
const SKIP_SAVED_KEY = 'cevee_recruiter_skip_saved';
const SKIP_CONTACTED_KEY = 'cevee_recruiter_skip_contacted';
const SAVED_SEARCHES_KEY = 'cevee_recruiter_saved_searches';
const ACTIVE_SEARCH_KEY = 'cevee_recruiter_active_search';
const MAX_SAVED_SEARCHES = 8;

interface SavedSearch {
  id: string;
  name: string;
  query: string;
  filters: QuickFilterState;
  createdAt: number;
  source: 'filters' | 'jd_upload' | 'manual' | 'suggestion';
}

function loadSavedSearches(): SavedSearch[] {
  try {
    const raw = localStorage.getItem(SAVED_SEARCHES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}
function persistSavedSearches(searches: SavedSearch[]) {
  try { localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(searches.slice(0, MAX_SAVED_SEARCHES))); } catch { /* */ }
}
function loadActiveSearchId(): string | null {
  try { return localStorage.getItem(ACTIVE_SEARCH_KEY); } catch { return null; }
}
function persistActiveSearchId(id: string | null) {
  try { if (id) localStorage.setItem(ACTIVE_SEARCH_KEY, id); else localStorage.removeItem(ACTIVE_SEARCH_KEY); } catch { /* */ }
}
function autoName(filters: QuickFilterState, query: string, source: SavedSearch['source']): string {
  if (source === 'jd_upload') return 'JD Upload';
  const parts = [...filters.role, ...filters.level, ...filters.location, ...filters.skills];
  if (parts.length) return parts.slice(0, 4).join(' · ');
  if (query.length > 60) return query.slice(0, 55) + '…';
  return query || 'Search';
}

function loadQuickFilters(): QuickFilterState {
  try {
    const saved = localStorage.getItem(QUICK_FILTER_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        location: parsed.location || [],
        role: parsed.role || [],
        level: parsed.level || [],
        skills: parsed.skills || [],
      };
    }
  } catch { /* ignore */ }
  return { location: [], role: [], level: [], skills: [] };
}

function persistQuickFilters(f: QuickFilterState) {
  try {
    const hasAny = f.location.length || f.role.length || f.level.length || f.skills.length;
    if (hasAny) {
      localStorage.setItem(QUICK_FILTER_STORAGE_KEY, JSON.stringify(f));
    } else {
      localStorage.removeItem(QUICK_FILTER_STORAGE_KEY);
    }
  } catch { /* ignore */ }
}

function buildFilterQuery(f: QuickFilterState): string {
  const parts: string[] = [];
  if (f.level.length) parts.push(f.level.join(', '));
  if (f.role.length) parts.push(f.role.join(', '));
  if (f.location.length) parts.push(f.location.join(', '));
  if (f.skills.length) parts.push(f.skills.join(', '));
  return parts.join(' ');
}

interface FeedCandidate {
  cv_id: string;
  score: number;
  keywords: string[];
  preview: string;
  cv_language?: string;
  career_status?: string;
  base_locations?: string;
  preferred_locations?: string;
  role_preferences?: string;
  current_role?: string;
  current_level?: string;
  reach_out_status?: string;
  reach_out_id?: string;
  conversation_id?: string;
  explorer_id?: string;
  is_interested?: boolean;
}


interface CVImages {
  cv_id: string;
  images: string[];
  image_count: number;
}

interface CVKeywords {
  cv_id: string;
  keywords: string[];
  sections: Record<string, string[]>;
}

interface InterestedFeedProps {
}

const InterestedFeed: React.FC<InterestedFeedProps> = () => {
  const { t, i18n } = useTranslation('interestedFeed');
  const { apiCall, user, authReady } = useAuth();
  const { userProfile } = useUserProfile();
  const navigate = useNavigate();
  const isSignedIn = !!user;
  const isExplorer = isSignedIn && userProfile && !userProfile.is_recruiter;
  const [recruiterProfile, setRecruiterProfile] = useState<any>(null);

  useEffect(() => {
    if (!user || !authReady) return;
    (async () => {
      try {
        const resp = await apiCall(`${API_BASE}/api/v1/profiles/${user.userId}`);
        if (resp.ok) setRecruiterProfile(await resp.json());
      } catch { /* ignore */ }
    })();
  }, [user?.userId, authReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const isProfileIncomplete = recruiterProfile && recruiterProfile.is_recruiter && (
    !recruiterProfile.first_name || !recruiterProfile.last_name || !recruiterProfile.company_name ||
    !recruiterProfile.recruiter_background ||
    !recruiterProfile.consents?.recruiter_profile_visibility?.granted
  );
  const [urlParams] = useSearchParams();
  const tabParam = urlParams.get('tab');
  const initialTab = (['feed', 'saved'] as const).includes(tabParam as any)
    ? (tabParam as 'feed' | 'saved')
    : 'feed';
  const [activeTab, setActiveTab] = useState<'feed' | 'interested' | 'saved'>(initialTab);
  const [candidates, setCandidates] = useState<FeedCandidate[]>([]);
  const [discoverCandidates, setDiscoverCandidates] = useState<FeedCandidate[]>([]);
  const [savedCandidates, setSavedCandidates] = useState<FeedCandidate[]>([]);
  const [savedCvIds, setSavedCvIds] = useState<Set<string>>(new Set());
  const [savedLoading, setSavedLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [discoverLoading, setDiscoverLoading] = useState(false);
  const initialLoadDone = useRef(false);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companyRoles, setCompanyRoles] = useState<string[]>([]);
  const [companyLevels, setCompanyLevels] = useState<string[]>([]);
  const [companyLocations, setCompanyLocations] = useState<string[]>([]);
  const [noCompany, setNoCompany] = useState(false);
  const [interestedCount, setInterestedCount] = useState(0);
  const [selectedCV, setSelectedCV] = useState<string | null>(null);
  const feedListScrollYRef = useRef(0);
  const detailOpenedFromListRef = useRef(false);
  const scrollDetailToTopRef = useRef(false);
  const [cvImages, setCvImages] = useState<Record<string, CVImages>>({});
  const [cvKeywords, setCvKeywords] = useState<Record<string, CVKeywords>>({});
  const [langFilter, setLangFilter] = useState<'all' | 'de' | 'en'>(() => readRecruiterLangFilterTab());

  // ── Job Description Upload ──
  const [jdUploading, setJdUploading] = useState(false);
  const jdFileRef = useRef<HTMLInputElement>(null);

  const cleanJdText = (raw: string) =>
    raw
      .replace(/[*_#~`>|]/g, '')       // strip markdown formatting chars
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // [link text](url) → link text
      .replace(/•|-|-|·|■|□|►|▪/g, ', ') // bullets/dashes → comma
      .replace(/\n{2,}/g, '\n')         // collapse blank lines
      .replace(/[ \t]{2,}/g, ' ')       // collapse spaces
      .replace(/^[\s,]+|[\s,]+$/g, '')  // trim leading/trailing junk
      .trim();

  const pruneFiltersForJd = (jdText: string): QuickFilterState => {
    const lower = jdText.toLowerCase();
    const pruned: QuickFilterState = {
      location: quickFilters.location.filter((v) => lower.includes(v.toLowerCase())),
      role: quickFilters.role.filter((v) => lower.includes(v.toLowerCase())),
      level: quickFilters.level.filter((v) => lower.includes(v.toLowerCase())),
      skills: quickFilters.skills.filter((v) => lower.includes(v.toLowerCase())),
    };
    setQuickFilters(pruned);
    return pruned;
  };

  const handleJdUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || jdUploading) return;
    e.target.value = '';

    const isPdf = file.type === 'application/pdf';
    const isImage = file.type.startsWith('image/');
    if (!isPdf && !isImage) return;

    setJdUploading(true);
    try {
      if (isPdf) {
        const arrayBuffer = await file.arrayBuffer();
        // @ts-ignore - pdfjsLib loaded from CDN
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const parts: string[] = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          parts.push(content.items.map((item: any) => item.str).join(' '));
        }
        const text = cleanJdText(parts.join('\n'));
        if (text.length > 20) {
          const sliced = text.slice(0, 3000);
          const pruned = pruneFiltersForJd(sliced);
          setSearchInput(sliced);
          const fname = file.name.replace(/\.[^.]+$/, '');
          saveCurrentSearch('jd_upload', `JD: ${fname}`, sliced, pruned);
          return;
        }
      }

      // Backend OCR for images (and scanned PDFs without embedded text)
      const formData = new FormData();
      formData.append('file', file);
      const resp = await apiCall(`${API_BASE}/api/v1/companies/extract-job-text`, {
        method: 'POST',
        body: formData,
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.text) {
          const cleaned = cleanJdText(data.text).slice(0, 3000);
          const pruned = pruneFiltersForJd(cleaned);
          setSearchInput(cleaned);
          const fname = file.name.replace(/\.[^.]+$/, '');
          saveCurrentSearch('jd_upload', `JD: ${fname}`, cleaned, pruned);
        }
      }
    } catch (err) {
      console.error('JD extraction failed:', err);
    } finally {
      setJdUploading(false);
    }
  };

  // ── AI Summary & Fit ──
  const [aiResults, setAiResults] = useState<Record<string, { summary: string; fit: string }>>({});
  const [aiLoading, setAiLoading] = useState<Record<string, boolean>>({});

  const generateAiSummary = async (cvId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (aiResults[cvId] || aiLoading[cvId]) return;
    setAiLoading((prev) => ({ ...prev, [cvId]: true }));
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/spotlights/ai-generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cv_id: cvId, language: i18n.language.startsWith('de') ? 'de' : 'en' }),
      });
      if (resp.ok) {
        const data = await resp.json();
        setAiResults((prev) => ({ ...prev, [cvId]: { summary: data.summary, fit: data.fit } }));
      } else if (resp.status === 429) {
        const apiError = await handleApiError(resp);
        if (apiError.rateLimitInfo) setRateLimitInfo(apiError.rateLimitInfo);
      } else {
        showToast('AI generation failed', 'error');
      }
    } catch {
      showToast('AI generation failed', 'error');
    } finally {
      setAiLoading((prev) => ({ ...prev, [cvId]: false }));
    }
  };
  const [showReachOut, setShowReachOut] = useState(false);
  const [reachOutSubmitting, setReachOutSubmitting] = useState(false);
  const [reachOutForm, setReachOutForm] = useState({ job_title: '', salary_range: '', message: '', nice_to_know: '' });
  const [reachOutSuggestions, setReachOutSuggestions] = useState<{ job_titles?: { title: string; location: string }[]; intro?: { en?: string; de?: string } } | null>(null);
  const dedupeAndSetSuggestions = (s: any) => {
    if (s && Array.isArray(s.job_titles)) {
      const seen = new Set<string>();
      s.job_titles = s.job_titles.filter((jt: any) => {
        const key = jt.title?.toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }
    setReachOutSuggestions(s);
  };
  const introTemplateRef = useRef('');
  const isTemplateMessageRef = useRef(false);
  const [skipContacted, setSkipContacted] = useState(() => {
    try { return localStorage.getItem(SKIP_CONTACTED_KEY) === '1'; } catch { return false; }
  });
  const [displayCount, setDisplayCount] = useState(8);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchInput, setSearchInput] = useState(() => {
    const activeId = loadActiveSearchId();
    if (activeId) {
      const saved = loadSavedSearches().find((s) => s.id === activeId);
      if (saved) return saved.query;
    }
    return buildFilterQuery(loadQuickFilters());
  });
  const [searchQuery, setSearchQuery] = useState(() => {
    const activeId = loadActiveSearchId();
    if (activeId) {
      const saved = loadSavedSearches().find((s) => s.id === activeId);
      if (saved) return saved.query;
    }
    return buildFilterQuery(loadQuickFilters());
  });
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
  const [skipSaved, setSkipSaved] = useState(() => {
    try { return localStorage.getItem(SKIP_SAVED_KEY) === '1'; } catch { return false; }
  });
  const [searchBounce, setSearchBounce] = useState(false);
  const tabBeforeSearch = useRef<'feed' | 'interested' | 'saved' | null>(null);

  // ── Quick Filters (CompanyExplore-style) ──
  const [quickFilters, setQuickFilters] = useState<QuickFilterState>(() => {
    const activeId = loadActiveSearchId();
    if (activeId) {
      const saved = loadSavedSearches().find((s) => s.id === activeId);
      if (saved) return saved.filters;
    }
    return loadQuickFilters();
  });
  const [expandedFilter, setExpandedFilter] = useState<string | null>(null);
  const [customInputs, setCustomInputs] = useState<Record<string, string>>({});
  const [locSuggestions, setLocSuggestions] = useState(() => shuffleAndPick(RECRUITER_LOCATION_SUGGESTIONS, 6));
  const [roleSuggestions, setRoleSuggestions] = useState(() => shuffleAndPick(RECRUITER_ROLE_SUGGESTIONS, 6));
  const [skillsSuggestions, setSkillsSuggestions] = useState(() => shuffleAndPick(RECRUITER_SKILLS_SUGGESTIONS, 6));

  const refreshSuggestions = (category: string) => {
    if (category === 'location') setLocSuggestions(shuffleAndPick(RECRUITER_LOCATION_SUGGESTIONS, 6));
    else if (category === 'role') setRoleSuggestions(shuffleAndPick(RECRUITER_ROLE_SUGGESTIONS, 6));
    else if (category === 'skills') setSkillsSuggestions(shuffleAndPick(RECRUITER_SKILLS_SUGGESTIONS, 6));
  };

  const toggleFilterChip = (category: keyof QuickFilterState, value: string) => {
    setQuickFilters((prev) => {
      const arr = prev[category];
      const next = { ...prev, [category]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
      const q = buildFilterQuery(next);
      setSearchInput(q);
      setSearchQuery(q);
      setSearchTrigger((n) => n + 1);
      setActiveSearchId(null);
      persistActiveSearchId(null);
      return next;
    });
  };

  const addCustomFilter = (category: keyof QuickFilterState) => {
    const val = (customInputs[category] || '').trim();
    if (!val) return;
    toggleFilterChip(category, val);
    setCustomInputs((prev) => ({ ...prev, [category]: '' }));
  };

  const clearAllFilters = () => {
    setQuickFilters({ location: [], role: [], level: [], skills: [] });
    setExpandedFilter(null);
    setCustomInputs({});
    setSearchInput('');
    setSearchQuery('');
    setSearchTrigger((n) => n + 1);
    setActiveSearchId(null);
    persistActiveSearchId(null);
  };

  const activeFilterCount = quickFilters.location.length + quickFilters.role.length + quickFilters.level.length + quickFilters.skills.length;

  const filtersMatch = (a: QuickFilterState, b: QuickFilterState) => {
    const eq = (x: string[], y: string[]) => x.length === y.length && x.every((v, i) => v === y[i]);
    return eq(a.location, b.location) && eq(a.role, b.role) && eq(a.level, b.level) && eq(a.skills, b.skills);
  };

  const matchesSavedSearch = (s: SavedSearch) =>
    s.query === searchInput.trim() && filtersMatch(s.filters, quickFilters);

  // ── Saved Searches ──
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>(loadSavedSearches);
  const [activeSearchId, setActiveSearchId] = useState<string | null>(loadActiveSearchId);

  const saveCurrentSearch = (source: SavedSearch['source'] = 'manual', nameOverride?: string, queryOverride?: string, filtersOverride?: QuickFilterState) => {
    const q = (queryOverride ?? searchInput).trim();
    if (!q && !activeFilterCount) return;
    const id = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    const filtersSnap = filtersOverride ? { ...filtersOverride } : { ...quickFilters };
    const entry: SavedSearch = {
      id,
      name: nameOverride || autoName(filtersSnap, q, source),
      query: q,
      filters: filtersSnap,
      createdAt: Date.now(),
      source,
    };
    setSavedSearches((prev) => {
      const next = [entry, ...prev.filter((s) => s.id !== id)].slice(0, MAX_SAVED_SEARCHES);
      persistSavedSearches(next);
      return next;
    });
    setActiveSearchId(id);
    persistActiveSearchId(id);
  };

  const loadSearch = (search: SavedSearch) => {
    setQuickFilters(search.filters);
    setSearchInput(search.query);
    setSearchQuery(search.query);
    setActiveSearchId(search.id);
    persistActiveSearchId(search.id);
    persistQuickFilters(search.filters);
    setSearchTrigger((n) => n + 1);
  };

  const deleteSearch = (id: string) => {
    setSavedSearches((prev) => {
      const next = prev.filter((s) => s.id !== id);
      persistSavedSearches(next);
      return next;
    });
    if (activeSearchId === id) {
      setActiveSearchId(null);
      persistActiveSearchId(null);
    }
  };

  // ── Auto-generate job chips from company suggestions ──
  const suggestionsProcessedRef = useRef(false);
  useEffect(() => {
    if (!reachOutSuggestions?.job_titles?.length || suggestionsProcessedRef.current) return;
    suggestionsProcessedRef.current = true;

    // Collect all unique locations from suggestions + company profile
    const allLocs: string[] = [];
    const locSeen = new Set<string>();
    for (const jt of reachOutSuggestions.job_titles!) {
      const l = jt.location?.trim();
      if (l && !locSeen.has(l.toLowerCase())) { locSeen.add(l.toLowerCase()); allLocs.push(l); }
    }
    for (const l of companyLocations) {
      if (!locSeen.has(l.toLowerCase())) { locSeen.add(l.toLowerCase()); allLocs.push(l); }
    }

    setSavedSearches((prev) => {
      const existingNames = new Set(prev.map((s) => s.name.toLowerCase()));
      const newChips: SavedSearch[] = [];

      for (const jt of reachOutSuggestions.job_titles!) {
        if (!jt.title) continue;
        const { role, level } = parseRoleLevel(jt.title);
        const name = jt.title;
        if (existingNames.has(name.toLowerCase())) continue;

        const locsForQuery = allLocs.slice(0, 3);
        const query = [jt.title, ...locsForQuery].join(' ');
        const filters: QuickFilterState = {
          role: role ? [role] : [],
          level: level ? [level] : [],
          location: [...allLocs],
          skills: [],
        };
        newChips.push({
          id: 'sug_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 5),
          name,
          query,
          filters,
          createdAt: Date.now(),
          source: 'suggestion',
        });
      }

      if (!newChips.length) return prev;
      const next = [...prev, ...newChips].slice(0, MAX_SAVED_SEARCHES);
      persistSavedSearches(next);
      return next;
    });
  }, [reachOutSuggestions]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Resizable panels ──
  const PANEL_STORAGE_KEY = 'cevee_feed_panel_pct';
  const [leftPanelPct, setLeftPanelPct] = useState<number>(() => {
    try { const s = localStorage.getItem(PANEL_STORAGE_KEY); return s ? Math.max(25, Math.min(70, Number(s))) : 40; } catch { return 40; }
  });
  const isDraggingPanel = useRef(false);
  const splitContainerRef = useRef<HTMLDivElement>(null);

  const onDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingPanel.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev: MouseEvent) => {
      if (!isDraggingPanel.current || !splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setLeftPanelPct(Math.max(25, Math.min(70, pct)));
    };
    const onUp = () => {
      isDraggingPanel.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      setLeftPanelPct((v) => { try { localStorage.setItem(PANEL_STORAGE_KEY, String(Math.round(v))); } catch {} return v; });
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  useEffect(() => { persistQuickFilters(quickFilters); }, [quickFilters]);
  useEffect(() => {
    try { localStorage.setItem(SKIP_SAVED_KEY, skipSaved ? '1' : '0'); } catch { /* ignore */ }
  }, [skipSaved]);
  useEffect(() => {
    try { localStorage.setItem(SKIP_CONTACTED_KEY, skipContacted ? '1' : '0'); } catch { /* ignore */ }
  }, [skipContacted]);

  // ── Scroll feed refs ──
  const reachOutCvId = useRef<string | null>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const cvSectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const preloadedCvsRef = useRef<Set<string>>(new Set());
  const scrollFromClickRef = useRef(false);
  const userDismissedDetail = useRef(false);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchInput.trim();
    if (q && activeTab !== 'feed') {
      tabBeforeSearch.current = activeTab;
      setActiveTab('feed');
    }
    setSearchQuery(q);
    setSearchTrigger((n) => n + 1);
    setSearchBounce(true);
    setTimeout(() => setSearchBounce(false), 200);
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearchQuery('');
    setActiveSearchId(null);
    persistActiveSearchId(null);
    if (tabBeforeSearch.current) {
      setActiveTab(tabBeforeSearch.current);
      tabBeforeSearch.current = null;
    }
  };

  useEffect(() => {
    persistRecruiterLangFilterTab(langFilter);
  }, [langFilter]);

  const submitReachOut = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isProfileIncomplete) { navigate('/recruiter-profile'); return; }
    if (!reachOutForm.job_title.trim()) { showToast(t('reach_out.job_title_required'), 'error'); return; }
    if (!reachOutForm.salary_range.trim()) { showToast(t('reach_out.salary_required'), 'error'); return; }
    if (!reachOutForm.message.trim()) { showToast(t('reach_out.message_required'), 'error'); return; }
    setReachOutSubmitting(true);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/recruiter/reach-out`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cv_id: reachOutCvId.current, job_title: reachOutForm.job_title, message: reachOutForm.message, salary_range: reachOutForm.salary_range, nice_to_know: reachOutForm.nice_to_know }),
      });
      if (resp.ok) {
        const data = await resp.json().catch(() => null);
        showToast(t('reach_out.sent'), 'success');
        setShowReachOut(false);
        setReachOutForm({ job_title: '', salary_range: '', message: '', nice_to_know: '' });
        const roCvId = reachOutCvId.current;
        if (roCvId) {
          const update = (c: FeedCandidate) => c.cv_id === roCvId
            ? { ...c, reach_out_status: 'candidate_sparked', reach_out_id: data?.vector_id || c.reach_out_id, conversation_id: data?.conversation_id || c.conversation_id }
            : c;
          setCandidates((prev) => prev.map(update));
          setDiscoverCandidates((prev) => prev.map(update));
          setSavedCandidates((prev) => prev.map(update));
        }
      } else if (resp.status === 429) {
        showToast(t('reach_out.rate_limit'), 'error', 6000);
      } else {
        const data = await resp.json().catch(() => null);
        showToast(data?.detail || t('reach_out.failed'), 'error');
      }
    } catch (err: any) {
      showToast(err.message || t('reach_out.failed'), 'error');
    } finally {
      setReachOutSubmitting(false);
    }
  };

  const SEARCH_CACHE_KEY = 'cevee_search_cache';
  const SEARCH_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  const getCachedSearch = (query: string) => {
    try {
      const raw = sessionStorage.getItem(SEARCH_CACHE_KEY);
      if (!raw) return null;
      const cache = JSON.parse(raw);
      if (cache.query === query && Date.now() - cache.ts < SEARCH_CACHE_TTL) return cache;
    } catch { /* ignore */ }
    return null;
  };

  const setCachedSearch = (query: string, feed: any, discover: any, meta: any) => {
    try {
      sessionStorage.setItem(SEARCH_CACHE_KEY, JSON.stringify({ query, feed, discover, meta, ts: Date.now() }));
    } catch { /* ignore */ }
  };

  const feedVersionRef = useRef(0);
  const lastFeedDataRef = useRef<any>(null);
  const lastDiscoverDataRef = useRef<any>(null);

  const loadFeed = async (query?: string): Promise<boolean> => {
    if (!user) return false;
    const version = ++feedVersionRef.current;
    if (!initialLoadDone.current) setLoading(true); else setListLoading(true);
    let rateLimited = false;
    try {
      const feedBody: Record<string, any> = { query: query || '', top_k: 20 };
      const feedPath = query ? 'interested-feed-search' : 'interested-feed';
      const resp = await apiCall(`${API_BASE}/api/v1/companies/${feedPath}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(feedBody),
      });
      if (version !== feedVersionRef.current) return false;
      if (!resp.ok) {
        if (resp.status === 401) {
          console.warn('⚠️ InterestedFeed: loadFeed got 401 - skipping (token not ready or expired)');
          return false;
        }
        if (resp.status === 429) {
          const apiError = await handleApiError(resp);
          if (apiError.rateLimitInfo) setRateLimitInfo(apiError.rateLimitInfo);
          rateLimited = true;
        } else {
          const text = await resp.text();
          if (resp.status === 404 || text.includes('no company')) {
            setNoCompany(true);
          } else if (resp.status === 403) {
            setNoCompany(true);
          } else {
            showToast(t('errors.load_failed'), 'error');
          }
        }
        return rateLimited;
      }
      setRateLimitInfo(null);
      const data = await resp.json();
      if (data.success) {
        setCandidates(data.candidates || []);
        setCompanyName(data.company_name || null);
        setInterestedCount(data.interested_count || 0);
        lastFeedDataRef.current = {
          candidates: data.candidates || [],
          company_name: data.company_name || null,
          interested_count: data.interested_count || 0,
          reach_out_suggestions: data.reach_out_suggestions || null,
        };
        if (data.reach_out_suggestions) {
          dedupeAndSetSuggestions(data.reach_out_suggestions);
          const rawTitles = (data.reach_out_suggestions.job_titles || []).map((jt: any) => jt.title).filter(Boolean);
          if (rawTitles.length && !companyRoles.length) {
            const roles: string[] = [];
            const levels = new Set<string>();
            for (const raw of rawTitles) {
              const subTitles = raw.split(/\s*[&,/]\s*/).map((s: string) => s.trim()).filter(Boolean);
              for (const t of subTitles) {
                const { role, level } = parseRoleLevel(t);
                if (role && !roles.includes(role)) roles.push(role);
                if (level) levels.add(level);
              }
            }
            setCompanyRoles(roles);
            if (levels.size) setCompanyLevels(Array.from(levels));
          }
        }
        if (data.company_location && !companyLocations.length) {
          setCompanyLocations(data.company_location.split(',').map((s: string) => s.trim()).filter(Boolean));
        }
        setNoCompany(false);
      }
    } catch (e: any) {
      if (version !== feedVersionRef.current) return false;
      const msg = e?.message || '';
      if (msg.includes('404') || msg.includes('no company') || msg.includes('403')) {
        setNoCompany(true);
      } else if (e?.name !== 'AbortError') {
        showToast(t('errors.load_failed'), 'error');
      }
    } finally {
      if (version === feedVersionRef.current) {
        setLoading(false);
        setListLoading(false);
        initialLoadDone.current = true;
      }
    }
    return rateLimited;
  };

  const discoverVersionRef = useRef(0);

  const loadDiscover = async (query?: string) => {
    if (!user) return;
    const version = ++discoverVersionRef.current;
    setDiscoverLoading(true);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/companies/discover-candidates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: query || '', top_k: 20 }),
      });
      if (version !== discoverVersionRef.current) return;
      if (!resp.ok) {
        if (resp.status === 401) {
          console.warn('⚠️ InterestedFeed: loadDiscover got 401 - skipping');
          return;
        }
        const text = await resp.text();
        if (resp.status === 404 || text.includes('no company') || resp.status === 403) {
          setNoCompany(true);
        }
        return;
      }
      const data = await resp.json();
      if (data.success) {
        setDiscoverCandidates(data.candidates || []);
        lastDiscoverDataRef.current = { candidates: data.candidates || [] };
        if (!companyName) setCompanyName(data.company_name || null);
        if (data.reach_out_suggestions) dedupeAndSetSuggestions(data.reach_out_suggestions);
        setNoCompany(false);
      }
    } catch (e: any) {
      if (version !== discoverVersionRef.current) return;
      const msg = e?.message || '';
      if (msg.includes('404') || msg.includes('no company') || msg.includes('403')) {
        setNoCompany(true);
      }
    } finally {
      if (version === discoverVersionRef.current) {
        setDiscoverLoading(false);
      }
    }
  };

  const loadSavedCandidates = async () => {
    if (!user) return;
    setSavedLoading(true);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/profiles/me/saved-cvs`);
      if (!resp.ok) return;
      const data = await resp.json();
      const savedList: { cv_id: string; prompt: string; saved_at: number }[] = data.saved_cvs || [];
      const cvIds = savedList.map((s) => s.cv_id);
      setSavedCvIds(new Set<string>(cvIds));

      if (cvIds.length === 0) { setSavedCandidates([]); return; }

      const BATCH = 50;
      const allCandidates: any[] = [];
      for (let i = 0; i < cvIds.length; i += BATCH) {
        const chunk = cvIds.slice(i, i + BATCH);
        const enrichResp = await apiCall(`${API_BASE}/api/v1/companies/saved-candidates`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cv_ids: chunk }),
        });
        if (enrichResp.ok) {
          const enrichData = await enrichResp.json();
          allCandidates.push(...(enrichData.candidates || []));
        }
      }
      setSavedCandidates(allCandidates);
    } catch { /* ignore */ }
    finally { setSavedLoading(false); }
  };

  const toggleSaveCandidate = async (cvId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) return;
    const wasSaved = savedCvIds.has(cvId);
    const nextIds = new Set(savedCvIds);
    if (wasSaved) { nextIds.delete(cvId); } else { nextIds.add(cvId); }
    setSavedCvIds(nextIds);

    if (wasSaved) {
      setSavedCandidates((prev) => prev.filter((c) => c.cv_id !== cvId));
    } else {
      const match = [...candidates, ...discoverCandidates].find((c) => c.cv_id === cvId);
      if (match) setSavedCandidates((prev) => [...prev, match]);
    }

    try {
      if (wasSaved) {
        await apiCall(`${API_BASE}/api/v1/profiles/me/saved-cvs/${cvId}`, { method: 'DELETE' });
      } else {
        await apiCall(`${API_BASE}/api/v1/profiles/me/saved-cvs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cv_id: cvId, prompt: '' }),
        });
      }
    } catch {
      setSavedCvIds(savedCvIds);
      if (!wasSaved) { setSavedCandidates((prev) => prev.filter((c) => c.cv_id !== cvId)); }
    }
  };

  const isFirstSearchRef = useRef(true);

  useEffect(() => {
    if (!user || isExplorer || !authReady) return;
    const query = searchQuery.trim() || undefined;
    const queryKey = query || '';

    if (isFirstSearchRef.current) {
      isFirstSearchRef.current = false;
      if (query) {
        const cached = getCachedSearch(queryKey);
        if (cached) {
          if (cached.feed) {
            setCandidates(cached.feed.candidates || []);
            setCompanyName(cached.feed.company_name || null);
            setInterestedCount(cached.feed.interested_count || 0);
            if (cached.feed.reach_out_suggestions) dedupeAndSetSuggestions(cached.feed.reach_out_suggestions);
          }
          if (cached.discover) {
            setDiscoverCandidates(cached.discover.candidates || []);
          }
          if (cached.meta) {
            if (cached.meta.companyRoles?.length) setCompanyRoles(cached.meta.companyRoles);
            if (cached.meta.companyLevels?.length) setCompanyLevels(cached.meta.companyLevels);
            if (cached.meta.companyLocations?.length) setCompanyLocations(cached.meta.companyLocations);
          }
          setLoading(false);
          initialLoadDone.current = true;
          setNoCompany(false);
          return;
        }
      }
    }

    if (!query) {
      setLoading(false);
      initialLoadDone.current = true;
      return;
    }

    (async () => {
      const wasRateLimited = await loadFeed(query);
      if (!wasRateLimited) await loadDiscover(query);
      if (lastFeedDataRef.current || lastDiscoverDataRef.current) {
        setCachedSearch(queryKey, lastFeedDataRef.current, lastDiscoverDataRef.current, {
          companyRoles, companyLevels, companyLocations,
        });
      }
    })();
  }, [user?.userId, isExplorer, searchQuery, searchTrigger, authReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const savedIdsLoadedRef = useRef(false);
  useEffect(() => {
    if (!user || isExplorer || !authReady || savedIdsLoadedRef.current) return;
    savedIdsLoadedRef.current = true;
    (async () => {
      try {
        const resp = await apiCall(`${API_BASE}/api/v1/profiles/me/saved-cvs`);
        if (resp.ok) {
          const data = await resp.json();
          const ids = new Set<string>((data.saved_cvs || []).map((s: any) => s.cv_id as string));
          setSavedCvIds(ids);
        }
      } catch { /* ignore */ }
      try {
        const resp = await apiCall(`${API_BASE}/api/v1/companies/my-company`);
        if (resp.ok) {
          const data = await resp.json();
          if (data.company?.id) setCompanyId(data.company.id);
        }
      } catch { /* ignore */ }
    })();
  }, [user?.userId, isExplorer, authReady]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab !== 'saved' || !user || isExplorer || !authReady) return;
    loadSavedCandidates();
  }, [activeTab, user?.userId, isExplorer, authReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Filtered list (shared by list view, detail navigation, infinite scroll) ──
  const applyFilters = (list: FeedCandidate[], tab: 'feed' | 'interested' | 'saved') => {
    let result = list;
    if (skipContacted) result = result.filter((c) => !c.reach_out_id);
    if (langFilter !== 'all') result = result.filter((c) => c.cv_language === langFilter);
    if (skipSaved && tab !== 'saved') result = result.filter((c) => !savedCvIds.has(c.cv_id));
    return result;
  };
  const currentSourceList = activeTab === 'feed' ? discoverCandidates : activeTab === 'saved' ? savedCandidates : candidates;
  const interestedCvIds = new Set(candidates.map(c => c.cv_id));
  const filteredNav = applyFilters(currentSourceList, activeTab);
  const feedCount = applyFilters(discoverCandidates, 'feed').length;
  const interestedFilteredCount = applyFilters(candidates, 'interested').length;
  const savedFilteredCount = applyFilters(savedCandidates, 'saved').length;

  // Reset display count and scroll state when switching tabs
  useEffect(() => {
    setDisplayCount(8);
    userDismissedDetail.current = false;
    preloadedCvsRef.current.clear();
    cvSectionRefs.current = {};
    rightPanelRef.current?.scrollTo(0, 0);
  }, [activeTab]);

  // Infinite scroll
  const loadingMoreRef = useRef(false);
  loadingMoreRef.current = loadingMore;
  const displayCountRef = useRef(displayCount);
  displayCountRef.current = displayCount;
  const filteredNavRef = useRef(filteredNav);
  filteredNavRef.current = filteredNav;
  const listLoadingRef = useRef(listLoading);
  listLoadingRef.current = listLoading;
  const isDiscoverLoadingRef = useRef(discoverLoading);
  isDiscoverLoadingRef.current = discoverLoading;

  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useCallback((el: HTMLDivElement | null) => {
    if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
    if (!el) return;
    observerRef.current = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || loadingMoreRef.current) return;
      if (listLoadingRef.current || isDiscoverLoadingRef.current) return;
      if (displayCountRef.current < filteredNavRef.current.length) {
        loadingMoreRef.current = true;
        setLoadingMore(true);
        setTimeout(() => {
          setDisplayCount(prev => prev + 6);
          setLoadingMore(false);
          loadingMoreRef.current = false;
        }, 300);
      }
    }, { rootMargin: '200px' });
    observerRef.current.observe(el);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Right panel infinite scroll sentinel
  const rightPanelObserverRef = useRef<IntersectionObserver | null>(null);
  const rightPanelSentinelRef = useCallback((el: HTMLDivElement | null) => {
    if (rightPanelObserverRef.current) { rightPanelObserverRef.current.disconnect(); rightPanelObserverRef.current = null; }
    if (!el) return;
    rightPanelObserverRef.current = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || loadingMoreRef.current) return;
      if (displayCountRef.current < filteredNavRef.current.length) {
        loadingMoreRef.current = true;
        setLoadingMore(true);
        setTimeout(() => {
          setDisplayCount(prev => prev + 6);
          setLoadingMore(false);
          loadingMoreRef.current = false;
        }, 300);
      }
    }, { root: rightPanelRef.current, rootMargin: '400px' });
    rightPanelObserverRef.current.observe(el);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCVData = async (cvId: string) => {
    if (cvImages[cvId] && cvKeywords[cvId]) return;
    try {
      const [imgResp, contentResp] = await Promise.all([
        apiCall(`${API_BASE}/api/v1/cv/${cvId}/images`),
        apiCall(`${API_BASE}/api/v1/cv/${cvId}/content?include_keywords=false`),
      ]);
      if (imgResp.ok) {
        const imgData = await imgResp.json();
        if (imgData.images) setCvImages((prev) => ({ ...prev, [cvId]: imgData }));
      }
      if (contentResp.ok) {
        const contentData = await contentResp.json();
        const kwData: CVKeywords = {
          cv_id: cvId,
          keywords: contentData.metadata?.keywords || [],
          sections: contentData.metadata?.sections_keywords || {},
        };
        setCvKeywords((prev) => ({ ...prev, [cvId]: kwData }));
      }
    } catch (e) {
      console.error('Failed to load CV data:', e);
    }
  };

  // ── CV navigation helpers ─────────────────────────────────
  const currentIdx = selectedCV ? filteredNav.findIndex((c) => c.cv_id === selectedCV) : -1;
  const hasPrev = currentIdx > 0;
  const hasNext = currentIdx >= 0 && currentIdx < filteredNav.length - 1;

  const navigateCV = useCallback((direction: 'prev' | 'next') => {
    const idx = filteredNav.findIndex((c) => c.cv_id === selectedCV);
    const nextIdx = direction === 'prev' ? idx - 1 : idx + 1;
    if (nextIdx < 0 || nextIdx >= filteredNav.length) return;
    const nextCvId = filteredNav[nextIdx].cv_id;
    setSelectedCV(nextCvId);
    loadCVData(nextCvId);
    scrollFromClickRef.current = true;
    cvSectionRefs.current[nextCvId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => { scrollFromClickRef.current = false; }, 800);
    const preloadIdx = direction === 'next' ? nextIdx + 1 : nextIdx - 1;
    if (preloadIdx >= 0 && preloadIdx < filteredNav.length) {
      loadCVData(filteredNav[preloadIdx].cv_id);
    }
  }, [filteredNav, selectedCV]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard navigation
  useEffect(() => {
    if (!selectedCV) return;
    const handleKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); navigateCV('prev'); }
      if (e.key === 'ArrowRight') { e.preventDefault(); navigateCV('next'); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedCV, navigateCV]);

  // Back to list: restore saved scroll position
  useLayoutEffect(() => {
    if (selectedCV) {
        scrollDetailToTopRef.current = false;
      return;
    }
    if (!detailOpenedFromListRef.current) return;
    detailOpenedFromListRef.current = false;
    window.scrollTo(0, feedListScrollYRef.current);
  }, [selectedCV]);

  const handleSelectCV = (cvId: string) => {
    userDismissedDetail.current = false;
    if (typeof window !== 'undefined') {
      feedListScrollYRef.current = window.scrollY;
      detailOpenedFromListRef.current = true;
    }
    setSelectedCV(cvId);
    loadCVData(cvId);
    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    scrollFromClickRef.current = true;
    if (isDesktop && rightPanelRef.current) {
      const panelTop = rightPanelRef.current.getBoundingClientRect().top + window.scrollY;
      if (window.scrollY > panelTop) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
    setTimeout(() => {
      cvSectionRefs.current[cvId]?.scrollIntoView({
        behavior: isDesktop ? 'smooth' : 'auto',
        block: 'start',
      });
      setTimeout(() => { scrollFromClickRef.current = false; }, isDesktop ? 800 : 100);
    }, 50);
    const idx = filteredNav.findIndex((c) => c.cv_id === cvId);
    if (idx >= 0 && idx + 1 < filteredNav.length) loadCVData(filteredNav[idx + 1].cv_id);
  };

  // ── Keep selectedCV in sync with filtered list (desktop only) ──
  const filteredNavIds = filteredNav.map(c => c.cv_id).join(',');
  useEffect(() => {
    if (userDismissedDetail.current) return;
    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    if (!isDesktop) return;
    if (filteredNav.length === 0) { setSelectedCV(null); return; }
    if (selectedCV && !filteredNav.some(c => c.cv_id === selectedCV)) {
      setSelectedCV(null);
    }
  }, [filteredNavIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Preload active CV + 1 ahead ──
  useEffect(() => {
    if (!selectedCV || filteredNav.length === 0) return;
    const idx = filteredNav.findIndex(c => c.cv_id === selectedCV);
    if (idx < 0) return;
    for (let i = 0; i <= 1; i++) {
      const target = filteredNav[idx + i];
      if (!target) break;
      if (!preloadedCvsRef.current.has(target.cv_id)) {
        preloadedCvsRef.current.add(target.cv_id);
        loadCVData(target.cv_id);
      }
    }
  }, [selectedCV, filteredNavIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Visibility-based preload removed - selection + 1 ahead is enough

  // ── Track active CV via scroll events + IntersectionObserver backup ──
  useEffect(() => {
    const panel = rightPanelRef.current;
    if (!panel) return;

    let rafId: number;
    const updateActiveCV = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (scrollFromClickRef.current) return;
        const panelRect = panel.getBoundingClientRect();
        const targetY = panelRect.top + panelRect.height * 0.33;
        let closestId: string | null = null;
        let closestDist = Infinity;
        for (const [cvId, el] of Object.entries(cvSectionRefs.current)) {
          if (!el) continue;
          const rect = el.getBoundingClientRect();
          const dist = Math.abs(rect.top + rect.height * 0.2 - targetY);
          if (dist < closestDist) { closestDist = dist; closestId = cvId; }
        }
        if (closestId) setSelectedCV(closestId);
      });
    };

    panel.addEventListener('scroll', updateActiveCV, { passive: true });
    window.addEventListener('scroll', updateActiveCV, { passive: true });

    const observer = new IntersectionObserver(
      () => updateActiveCV(),
      { root: panel, threshold: [0, 0.2, 0.4, 0.6, 0.8, 1.0] }
    );
    const timer = setTimeout(() => {
      panel.querySelectorAll('[data-cv-id]').forEach(el => observer.observe(el));
    }, 300);

    return () => {
      panel.removeEventListener('scroll', updateActiveCV);
      window.removeEventListener('scroll', updateActiveCV);
      clearTimeout(timer);
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [displayCount, filteredNavIds]); // re-setup when sections change

  // ── Left panel auto-scroll to match active CV ──
  useEffect(() => {
    if (!selectedCV || scrollFromClickRef.current) return;
    const el = cardRefs.current[selectedCV];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedCV]);

  // ── Skeleton Background ─────────────────────────────────────

  const SkeletonHeader = () => (
    <div className="flex items-center justify-between gap-2 mb-2 sm:mb-4 flex-wrap">
      <div className="flex gap-1.5 sm:gap-2">
        <div className="flex items-center gap-0.5 sm:gap-1 px-2 py-1 sm:px-3 sm:py-1.5 rounded-full text-[11px] lg:text-[13px] font-medium bg-slate-800 text-white shadow-sm">
          <Users className="w-3 h-3 lg:w-3.5 lg:h-3.5 shrink-0" /> {t('tabs.feed')}
        </div>
        <div className="flex items-center gap-0.5 sm:gap-1 px-2 py-1 sm:px-3 sm:py-1.5 rounded-full text-[11px] lg:text-[13px] font-medium bg-pink-50 text-pink-500">
          <Heart className="w-3 h-3 lg:w-3.5 lg:h-3.5 shrink-0" /> {t('tabs.interested')}
        </div>
        <div className="flex items-center gap-0.5 sm:gap-1 px-2 py-1 sm:px-3 sm:py-1.5 rounded-full text-[11px] lg:text-[13px] font-medium bg-slate-100 text-slate-600">
          <Bookmark className="w-3 h-3 lg:w-3.5 lg:h-3.5 shrink-0" /> {t('tabs.saved')}
        </div>
      </div>
      <div className="flex items-center gap-2.5 sm:gap-3">
        <div className="flex items-center gap-1">
          <Building2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-300" />
          <div className="h-4 w-24 sm:w-32 rounded bg-slate-200 animate-pulse" />
        </div>
        <span className="text-xs sm:text-sm text-slate-400 flex items-center gap-1 shrink-0">
          <Star className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-amber-400 text-amber-400" /> Spotlight
        </span>
      </div>
    </div>
  );

  const SkeletonFeed = ({ hideHeader = false }: { hideHeader?: boolean }) => {
    const cards = Array.from({ length: 6 });
    return (
      <>
        {!hideHeader && <SkeletonHeader />}
        <div className="space-y-2 sm:space-y-3 blur-[6px] select-none pointer-events-none mt-3 sm:mt-4" aria-hidden="true">
          {cards.map((_, i) => (
            <div key={i} className="bg-white rounded-xl p-3 sm:p-3.5 shadow-sm border border-slate-100">
              {/* Top row: bookmark + badge left, language right */}
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  <Bookmark className="w-3.5 h-3.5 text-slate-300" />
                  <span className="text-xs lg:text-sm font-medium text-pink-500 bg-pink-50 px-1.5 sm:px-2 py-0.5 rounded-full flex items-center gap-0.5">
                    <Heart className="w-2.5 h-2.5 fill-pink-500" /> {t('card.interested')}
                  </span>
                </div>
                <span className="text-xs lg:text-sm text-slate-400 uppercase">{['de', 'en', 'de', 'en', 'de', 'de'][i]}</span>
              </div>
              {/* Role headline */}
              <p className="text-sm lg:text-[15px] font-semibold text-slate-800 leading-snug mb-0.5">
                {['Marketing Manager', 'Software Engineer', 'UX Designer', 'Product Manager', 'Data Analyst', 'Sales Lead'][i]}
              </p>
              {/* Level + location */}
              <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 text-[11px] lg:text-xs mb-1.5">
                <span className="text-slate-400">{['Mid-Level (2-5 Jahre)', 'Senior (5-8 Jahre)', 'Junior (0-2 Jahre)', 'Lead (8+ Jahre)', 'Mid-Level (2-5 Jahre)', 'Senior (5-8 Jahre)'][i]}</span>
                <span className="text-slate-500 flex items-center gap-0.5">
                  <MapPin className="w-3 h-3 text-slate-400 shrink-0" />{['Berlin', 'München', 'Hamburg', 'Köln', 'Frankfurt', 'Stuttgart'][i]}
                </span>
              </div>
              {/* Seeking section */}
              <div className="flex items-start gap-1 text-[11px] lg:text-xs mb-2">
                <Heart className="w-3 h-3 text-pink-400 shrink-0 mt-0.5" />
                <p className="text-slate-600 font-medium leading-snug">
                  {['Senior Marketing · Remote', 'Full-Stack Dev · Berlin', 'Lead UX · Hamburg', 'Head of Product · München', 'Senior Analyst · Remote', 'VP Sales · DACH'][i]}
                </p>
              </div>
              {/* Keywords */}
              <div className="flex items-center flex-wrap gap-1">
                {[['Growth', 'SEO', 'Content'], ['React', 'Node.js', 'TypeScript'], ['Figma', 'User Research', 'Prototyping'], ['Agile', 'Roadmapping', 'OKRs'], ['Python', 'SQL', 'Tableau'], ['Enterprise', 'SaaS', 'Pipeline']][i].map((kw) => (
                  <span key={kw} className="text-[11px] lg:text-xs px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">{kw}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  const SkeletonCV = ({ blur = false }: { blur?: boolean }) => (
    <div className={blur ? 'blur-[6px] select-none pointer-events-none' : ''} aria-hidden={blur}>
      <div className="bg-white rounded-xl border border-slate-200 p-3 mb-3 relative overflow-hidden">
        {!blur && <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent z-10" />}
        {/* Top row: bookmark + badge left, contact button right */}
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <div className="h-3.5 w-3.5 rounded bg-slate-200 animate-pulse" />
            <div className="h-5 w-16 rounded-full bg-pink-100 animate-pulse" />
            <div className="h-3 w-5 rounded bg-slate-100 animate-pulse" />
          </div>
          <div className="h-7 w-32 rounded-lg bg-pink-100 animate-pulse" />
        </div>
        {/* Role */}
        <div className="h-[14px] w-2/3 bg-slate-200 rounded animate-pulse mb-1" />
        {/* Level */}
        <div className="h-3 w-28 bg-slate-100 rounded animate-pulse mb-1.5" />
        {/* Location: base → preferred */}
        <div className="flex items-center gap-1 mb-1.5">
          <div className="h-3 w-3 rounded bg-slate-100 animate-pulse shrink-0" />
          <div className="h-3 w-16 bg-slate-100 rounded animate-pulse" />
          <div className="h-3 w-3 rounded bg-pink-100 animate-pulse shrink-0" />
          <div className="h-3 w-3 rounded bg-slate-100 animate-pulse shrink-0" />
          <div className="h-3 w-20 bg-slate-100 rounded animate-pulse" />
        </div>
        {/* Seeking */}
        <div className="flex items-start gap-1.5">
          <div className="h-3.5 w-3.5 rounded bg-pink-100 animate-pulse shrink-0 mt-0.5" />
          <div className="h-3 w-3/4 bg-slate-100 rounded animate-pulse" />
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden relative">
        {!blur && <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent z-10" />}
        <div className="aspect-[210/297] bg-gradient-to-b from-slate-50 to-slate-100 relative">
          <div className="absolute inset-0 p-8 space-y-4">
            <div className="h-6 w-48 bg-slate-200 rounded animate-pulse" />
            <div className="h-3 w-32 bg-slate-200 rounded animate-pulse" />
            <div className="mt-6 space-y-2">
              <div className="h-3 w-full bg-slate-200/60 rounded animate-pulse" />
              <div className="h-3 w-full bg-slate-200/60 rounded animate-pulse" />
              <div className="h-3 w-3/4 bg-slate-200/60 rounded animate-pulse" />
            </div>
            <div className="mt-4 space-y-2">
              <div className="h-4 w-36 bg-slate-200 rounded animate-pulse" />
              <div className="h-3 w-full bg-slate-200/60 rounded animate-pulse" />
              <div className="h-3 w-full bg-slate-200/60 rounded animate-pulse" />
              <div className="h-3 w-5/6 bg-slate-200/60 rounded animate-pulse" />
            </div>
            <div className="mt-4 space-y-2">
              <div className="h-4 w-28 bg-slate-200 rounded animate-pulse" />
              <div className="h-3 w-full bg-slate-200/60 rounded animate-pulse" />
              <div className="h-3 w-2/3 bg-slate-200/60 rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Logged Out Skeleton ────────────────────────────────────

  if (!isSignedIn) {
    return <RecruiterLoggedOutGate returnTo="/candidate-search" />;
  }

  // ── Explorer Gate ──────────────────────────────────────────

  if (isExplorer) {
    return (
      <RecruiterPageShell contentClassName="relative">
        <div className="pointer-events-none select-none opacity-50 blur-[2px]" aria-hidden>
          <div className="lg:flex lg:gap-6">
            <div className="w-full lg:w-2/5 lg:border-r lg:border-slate-200 lg:pr-4">
              <SkeletonFeed hideHeader />
            </div>
            <div className="hidden lg:block lg:w-3/5 lg:pl-4">
              <SkeletonCV blur />
            </div>
          </div>
        </div>

        <div className="fixed inset-0 z-10 flex items-center justify-center px-4 pointer-events-none">
          <div className="pointer-events-auto w-full max-w-md">
            <RecruiterGateCard
              icon={<Building2 className="size-6 text-white" />}
              title={t('empty.recruiter_only_title')}
              description={
                <>
                  {t('empty.recruiter_only_desc')}
                  <span className="mt-2 block text-xs text-slate-400">{t('empty.recruiter_only_cta')}</span>
                </>
              }
            >
              <button
                type="button"
                onClick={() => navigate('/?tab=inspire')}
                className="mt-8 flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 py-3 text-sm font-semibold text-white shadow-lg shadow-pink-500/20 transition-all hover:shadow-xl hover:shadow-pink-500/25"
              >
                Inspire →
              </button>
            </RecruiterGateCard>
          </div>
        </div>
      </RecruiterPageShell>
    );
  }

  // ── Loading ────────────────────────────────────────────────

  const showInitialLoading = loading || (isSignedIn && !isExplorer && !initialLoadDone.current);

  if (showInitialLoading) {
    return <RecruiterLoadingScreen icon={Search} />;
  }

  // ── No Company State ───────────────────────────────────────

  if (noCompany) {
    return (
      <RecruiterPageShell>
        <RecruiterInlineEmpty
          icon={Building2}
          title={t('empty.no_company_title')}
          description={t('empty.no_company_desc')}
          action={
            <Link
              to="/company/manage"
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800"
            >
              <Building2 className="size-4" /> {t('empty.create_company')}
            </Link>
          }
        />
      </RecruiterPageShell>
    );
  }

  // ── Resolve selected candidate for detail panel ────────────
  // selectedCandidate is resolved per-CV in the scroll feed, no longer needed globally

  // ── Feed View (with desktop split) ────────────────────────

  return (
    <RecruiterPageShell>
      {/* Page toolbar — saved toggle + company (nav lives in sidebar) */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3 min-w-0">
          {companyName && (
            <Link to="/company/manage" className="text-sm font-medium text-slate-500 transition-colors hover:text-slate-800 truncate">
              {companyName}
            </Link>
          )}
          <button
            type="button"
            onClick={() => setActiveTab(activeTab === 'saved' ? 'feed' : 'saved')}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-medium transition-all ${
              activeTab === 'saved'
                ? 'bg-slate-900 text-white shadow-sm'
                : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Bookmark className="size-3.5" /> {t('tabs.saved')}
            {savedCvIds.size > 0 && (
              <span className={`rounded-full px-1.5 py-0.5 text-xs ${activeTab === 'saved' ? 'bg-white/20' : 'bg-slate-100'}`}>
                {activeTab === 'saved' && savedCandidates.length > 0 ? savedFilteredCount : savedCvIds.size}
              </span>
            )}
          </button>
        </div>
      </div>

      {rateLimitInfo && (
        <div className="mb-4">
          <RateLimitNotification
            rateLimitInfo={rateLimitInfo}
            feature="candidate_search"
            onClose={() => setRateLimitInfo(null)}
          />
        </div>
      )}

      {/* Search + filters toolbar — single card (Pipeline / HB style) */}
      <div className={`mb-4 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4 ${selectedCV ? 'hidden lg:block' : ''}`}>

        {/* Language + visibility toggles */}
        <div className="flex flex-wrap items-center gap-2 text-[13px]">
          <div className="flex items-center gap-1">
            {(['all', 'de', 'en'] as const).map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => setLangFilter(lang)}
                className={`rounded-lg px-2.5 py-1 transition-colors ${
                  langFilter === lang
                    ? 'bg-slate-900 font-medium text-white shadow-sm'
                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
              >
                {lang === 'all' ? t('filters.all_languages') : lang.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="hidden h-4 w-px bg-slate-200 sm:block" />
          {activeTab !== 'saved' && savedCvIds.size > 0 && (
            <button
              type="button"
              onClick={() => setSkipSaved((v) => !v)}
              className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 transition-colors ${
                skipSaved
                  ? 'bg-slate-900 font-medium text-white shadow-sm'
                  : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              <EyeOff className="size-3" />
              {t('filters.skip_saved_short')}
            </button>
          )}
          <button
            type="button"
            onClick={() => setSkipContacted((v) => !v)}
            className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 transition-colors ${
              skipContacted
                ? 'bg-slate-900 font-medium text-white shadow-sm'
                : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <EyeOff className="size-3" />
            {t('filters.skip_contacted_short')}
          </button>
        </div>

        {/* Search */}
        <div className="mt-3 border-t border-slate-100 pt-3">
      <form onSubmit={handleSearchSubmit}>
        <div className="flex items-start gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-2 sm:left-3 top-2.5 sm:top-3 w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 pointer-events-none" />
            <textarea
              ref={(el) => { if (el) { el.style.height = 'auto'; const h = Math.min(el.scrollHeight, 280); el.style.height = h + 'px'; el.style.overflowY = el.scrollHeight > 280 ? 'auto' : 'hidden'; } }}
              value={searchInput}
              onChange={(e) => { setSearchInput(e.target.value); const el = e.target; el.style.height = 'auto'; const h = Math.min(el.scrollHeight, 280); el.style.height = h + 'px'; el.style.overflowY = el.scrollHeight > 280 ? 'auto' : 'hidden'; }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSearchSubmit(e); } }}
              placeholder={t('search.placeholder')}
              rows={1}
              className={`w-full resize-none overflow-hidden rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-9 text-sm shadow-sm focus:border-slate-300 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-slate-900/10 sm:py-2.5 ${activeFilterCount > 0 ? 'border-slate-300' : 'border-slate-300'}`}
            />
            {searchInput && (
              <button
                type="button"
                onClick={handleSearchClear}
                className="absolute right-3 top-2.5 sm:top-3 text-slate-400 hover:text-slate-600"
                aria-label={t('search.clear')}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <input ref={jdFileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={handleJdUpload} className="hidden" />
          <button
            type="button"
            onClick={() => jdFileRef.current?.click()}
            disabled={jdUploading}
            className="flex-shrink-0 rounded-xl border border-slate-300 bg-white p-2 text-slate-500 shadow-sm transition-colors hover:border-slate-400 hover:text-slate-700 disabled:opacity-50 sm:p-2.5"
            title={t('search.upload_jd', { defaultValue: 'Upload Job Description (PDF/Image)' })}
          >
            {jdUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
          </button>
          <button
            type="submit"
            className={`flex-shrink-0 rounded-xl bg-slate-900 p-2.5 text-white shadow-sm transition-colors hover:bg-slate-800 sm:p-2.5 ${searchBounce ? 'scale-95 brightness-110' : ''}`}
          >
            <Search className="w-4 h-4" />
          </button>
        </div>
      </form>
        </div>

      {/* Saved Searches */}
      {(savedSearches.length > 0 || (searchInput.trim() || activeFilterCount > 0)) && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-slate-100 pt-3">
          <FolderOpen className="w-3.5 h-3.5 text-slate-300 shrink-0" />
          {savedSearches.map((s) => (
            <button
              key={s.id}
              onClick={() => loadSearch(s)}
              className={`group flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] lg:text-xs font-medium border transition-all max-w-[200px] ${
                activeSearchId === s.id && matchesSavedSearch(s)
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
              }`}
            >
              {s.source === 'suggestion' && <Briefcase className="w-2.5 h-2.5 shrink-0" />}
              {s.source === 'jd_upload' && <FileText className="w-2.5 h-2.5 shrink-0" />}
              <span className="truncate">{s.name}</span>
              <span
                onClick={(e) => { e.stopPropagation(); deleteSearch(s.id); }}
                className="text-slate-300 hover:text-red-400 shrink-0 ml-0.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >×</span>
            </button>
          ))}
          {(searchInput.trim() || activeFilterCount > 0) && !savedSearches.some((s) => matchesSavedSearch(s)) && (
            <button
              onClick={() => saveCurrentSearch(activeFilterCount > 0 ? 'filters' : 'manual')}
              className="flex items-center gap-0.5 rounded-full border border-dashed border-slate-300 px-2 py-0.5 text-[11px] font-medium text-slate-500 transition-colors hover:border-slate-400 hover:text-slate-700 lg:text-xs"
            >
              + Save Search
            </button>
          )}
        </div>
      )}

      {/* Quick Filters — compact toolbar row + single expand panel */}
      {activeTab !== 'saved' && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {([
              { category: 'location' as const, icon: MapPin, label: t('filters.location') },
              { category: 'role' as const, icon: Briefcase, label: t('filters.role') },
              { category: 'level' as const, icon: Zap, label: t('filters.level') },
              { category: 'skills' as const, icon: Target, label: t('filters.skills') },
            ]).map(({ category, icon: Icon, label }) => {
              const selected = quickFilters[category];
              const isExpanded = expandedFilter === category;
              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => setExpandedFilter(isExpanded ? null : category)}
                  className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                    isExpanded
                      ? 'bg-slate-900 text-white shadow-sm'
                      : selected.length > 0
                        ? 'border border-slate-300 bg-white text-slate-700 hover:border-slate-400'
                        : 'text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="size-3 shrink-0" />
                  <span>{label}</span>
                  {selected.length > 0 && (
                    <span className={`rounded-full px-1 py-px text-[10px] font-semibold tabular-nums ${
                      isExpanded ? 'bg-white/20 text-white' : 'bg-pink-100 text-pink-600'
                    }`}>
                      {selected.length}
                    </span>
                  )}
                  <ChevronDown className={`size-3 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>
              );
            })}
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="ml-auto inline-flex items-center gap-1 text-[11px] text-slate-400 transition-colors hover:text-slate-700"
              >
                <Filter className="size-3" />
                {t('filters.clear_all')}
              </button>
            )}
          </div>

          {!expandedFilter && activeFilterCount > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {(['location', 'role', 'level', 'skills'] as const).flatMap((category) =>
                quickFilters[category].map((v) => {
                  const display =
                    category === 'level'
                      ? (RECRUITER_LEVEL_OPTIONS.find((o) => o.value === v)
                          ? (i18n.language.startsWith('de')
                              ? RECRUITER_LEVEL_OPTIONS.find((o) => o.value === v)!.labelDe
                              : RECRUITER_LEVEL_OPTIONS.find((o) => o.value === v)!.labelEn)
                          : v)
                      : v;
                  return (
                    <button
                      key={`${category}-${v}`}
                      type="button"
                      onClick={() => toggleFilterChip(category, v)}
                      className="rounded-md border border-pink-100 bg-pink-50/80 px-1.5 py-0.5 text-[10px] font-medium text-pink-700 transition-colors hover:border-pink-200 hover:bg-pink-100"
                    >
                      {display} ×
                    </button>
                  );
                }),
              )}
            </div>
          )}

          {expandedFilter === 'location' && (() => {
            const category = 'location' as const;
            const selected = quickFilters[category];
            const companySugs = companyLocations.filter((s) => !locSuggestions.includes(s));
            const suggestions = [...companySugs, ...locSuggestions];
            const inputVal = customInputs[category] || '';
            return (
              <div className="mt-2 rounded-lg border border-slate-200 p-2">
                <div className="mb-1.5 flex items-center justify-end">
                  <button type="button" onClick={() => refreshSuggestions(category)} className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600" title={t('filters.refresh_title')}>
                    <RefreshCw className="size-3" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {suggestions.map((s) => {
                    const isCompany = companyLocations.includes(s);
                    return (
                      <button key={s} type="button" onClick={() => toggleFilterChip(category, s)} className={`whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[10px] font-medium transition-colors ${selected.includes(s) ? 'border-slate-900 bg-slate-900 text-white' : isCompany ? 'border-slate-300 bg-slate-100 text-slate-700 hover:border-slate-400' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
                        {s}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-1.5 flex gap-1">
                  <input value={inputVal} onChange={(e) => setCustomInputs((prev) => ({ ...prev, [category]: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomFilter(category))} placeholder={t('filters.custom_placeholder', { label: t('filters.location').toLowerCase() })} className="min-w-0 flex-1 rounded-md border border-slate-300 px-2 py-1 text-[11px] outline-none focus:border-slate-300 focus:ring-1 focus:ring-slate-900/10" />
                  <button type="button" onClick={() => addCustomFilter(category)} className="rounded-md bg-slate-900 px-2 py-1 text-[10px] font-medium text-white hover:bg-slate-800">+</button>
                </div>
              </div>
            );
          })()}

          {expandedFilter === 'role' && (() => {
            const category = 'role' as const;
            const selected = quickFilters[category];
            const companySugs = companyRoles.filter((s) => !roleSuggestions.includes(s));
            const suggestions = [...companySugs, ...roleSuggestions];
            const inputVal = customInputs[category] || '';
            return (
              <div className="mt-2 rounded-lg border border-slate-200 p-2">
                <div className="mb-1.5 flex items-center justify-end">
                  <button type="button" onClick={() => refreshSuggestions(category)} className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600" title={t('filters.refresh_title')}>
                    <RefreshCw className="size-3" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {suggestions.map((s) => {
                    const isCompany = companyRoles.includes(s);
                    return (
                      <button key={s} type="button" onClick={() => toggleFilterChip(category, s)} className={`whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[10px] font-medium transition-colors ${selected.includes(s) ? 'border-slate-900 bg-slate-900 text-white' : isCompany ? 'border-slate-300 bg-slate-100 text-slate-700 hover:border-slate-400' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
                        {s}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-1.5 flex gap-1">
                  <input value={inputVal} onChange={(e) => setCustomInputs((prev) => ({ ...prev, [category]: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomFilter(category))} placeholder={t('filters.custom_placeholder', { label: t('filters.role').toLowerCase() })} className="min-w-0 flex-1 rounded-md border border-slate-300 px-2 py-1 text-[11px] outline-none focus:border-slate-300 focus:ring-1 focus:ring-slate-900/10" />
                  <button type="button" onClick={() => addCustomFilter(category)} className="rounded-md bg-slate-900 px-2 py-1 text-[10px] font-medium text-white hover:bg-slate-800">+</button>
                </div>
              </div>
            );
          })()}

          {expandedFilter === 'level' && (() => {
            const category = 'level' as const;
            const selected = quickFilters[category];
            return (
              <div className="mt-2 rounded-lg border border-slate-200 p-2">
                <div className="flex flex-wrap gap-1">
                  {[...RECRUITER_LEVEL_OPTIONS].sort((a, b) => {
                    const aComp = companyLevels.includes(a.value) ? 0 : 1;
                    const bComp = companyLevels.includes(b.value) ? 0 : 1;
                    return aComp - bComp;
                  }).map((opt) => {
                    const label = i18n.language.startsWith('de') ? opt.labelDe : opt.labelEn;
                    const isCompany = companyLevels.includes(opt.value);
                    return (
                      <button key={opt.value} type="button" onClick={() => toggleFilterChip(category, opt.value)} className={`whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[10px] font-medium transition-colors ${selected.includes(opt.value) ? 'border-slate-900 bg-slate-900 text-white' : isCompany ? 'border-slate-300 bg-slate-100 text-slate-700 hover:border-slate-400' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {expandedFilter === 'skills' && (() => {
            const category = 'skills' as const;
            const selected = quickFilters[category];
            const suggestions = skillsSuggestions;
            const inputVal = customInputs[category] || '';
            return (
              <div className="mt-2 rounded-lg border border-slate-200 p-2">
                <div className="mb-1.5 flex items-center justify-end">
                  <button type="button" onClick={() => refreshSuggestions(category)} className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600" title={t('filters.refresh_title')}>
                    <RefreshCw className="size-3" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {suggestions.map((s) => (
                    <button key={s} type="button" onClick={() => toggleFilterChip(category, s)} className={`whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[10px] font-medium transition-colors ${selected.includes(s) ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
                      {s}
                    </button>
                  ))}
                </div>
                <div className="mt-1.5 flex gap-1">
                  <input value={inputVal} onChange={(e) => setCustomInputs((prev) => ({ ...prev, [category]: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomFilter(category))} placeholder={t('filters.custom_placeholder', { label: t('filters.skills').toLowerCase() })} className="min-w-0 flex-1 rounded-md border border-slate-300 px-2 py-1 text-[11px] outline-none focus:border-slate-300 focus:ring-1 focus:ring-slate-900/10" />
                  <button type="button" onClick={() => addCustomFilter(category)} className="rounded-md bg-slate-900 px-2 py-1 text-[10px] font-medium text-white hover:bg-slate-800">+</button>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      </div>

      {/* ═══ Split view container ═══ */}
      <div ref={splitContainerRef} className="lg:flex lg:gap-0 lg:h-[calc(100vh-120px)]" style={{ minHeight: 'calc(100vh - 40px)' }}>
        {/* Left Panel: Cards */}
        <div
          className={`w-full lg:overflow-y-auto lg:h-full ${selectedCV ? 'hidden lg:block' : ''}`}
          style={{ flex: `0 0 ${leftPanelPct}%` }}
        >

      {/* Candidate List */}
      {(() => {
        const isFeedTab = activeTab === 'feed';
        const isSavedTab = activeTab === 'saved';
        const filtered = filteredNav;

        if ((isFeedTab && discoverLoading) || (isSavedTab && savedLoading) || (!isFeedTab && !isSavedTab && listLoading)) {
          return (
            <div className="space-y-2 sm:space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-white rounded-xl p-3 sm:p-3.5 shadow-sm border border-slate-200 relative overflow-hidden">
                  <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent z-10" />
                  {/* Row 1: Role + badges */}
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="min-w-0 flex-1">
                      <div className="h-[15px] w-3/4 rounded bg-slate-200 animate-pulse" />
                      <div className="h-3 w-28 rounded bg-slate-100 animate-pulse mt-1" />
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <div className="h-5 w-5 rounded-full bg-pink-100 animate-pulse" />
                      <div className="h-3.5 w-3.5 rounded bg-slate-200 animate-pulse" />
                      <div className="h-3 w-5 rounded bg-slate-100 animate-pulse" />
                    </div>
                  </div>
                  {/* Row 2: Location */}
                  <div className="flex items-center gap-1 mb-1.5">
                    <div className="h-3 w-3 rounded bg-slate-100 animate-pulse shrink-0" />
                    <div className="h-3 w-20 rounded bg-slate-100 animate-pulse" />
                    <div className="h-3 w-3 rounded bg-pink-100 animate-pulse shrink-0" />
                    <div className="h-3 w-3 rounded bg-slate-100 animate-pulse shrink-0" />
                    <div className="h-3 w-24 rounded bg-slate-100 animate-pulse" />
                  </div>
                  {/* Row 3: Seeking */}
                  <div className="flex items-start gap-1">
                    <div className="h-3 w-3 rounded bg-pink-100 animate-pulse shrink-0 mt-0.5" />
                    <div className="h-3 w-2/3 rounded bg-slate-100 animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          );
        }

        const hasSearched = searchQuery.trim().length > 0;

        return filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center lg:h-full py-20">
            {isSavedTab ? (
              <Bookmark className="w-7 h-7 text-slate-200 mb-2" />
            ) : !hasSearched && isFeedTab ? (
              <Search className="w-7 h-7 text-slate-200 mb-2" />
            ) : (
              <Heart className="w-7 h-7 text-slate-200 mb-2" />
            )}
            <p className="text-sm text-slate-400">
              {isSavedTab
                ? t('empty.no_saved')
                : isFeedTab && !hasSearched
                  ? t('empty.start_search')
                  : isFeedTab
                    ? t('empty.no_discover')
                    : (candidates.length > 0 ? t('empty.no_candidates_filtered') : t('empty.no_candidates'))}
            </p>
            <p className="text-xs text-slate-300 mt-1">
              {isSavedTab
                ? t('empty.no_saved_hint')
                : isFeedTab && !hasSearched
                  ? t('empty.start_search_hint')
                  : isFeedTab
                    ? t('empty.no_discover_hint')
                    : (candidates.length > 0 ? t('empty.try_other_filter') : t('empty.wait_for_interest'))}
            </p>
          </div>
        ) : (
          <div className="space-y-2 sm:space-y-3">
                {filtered.slice(0, displayCount).map((candidate) => {
            const showInterestedBadge = !!candidate.is_interested || interestedCvIds.has(candidate.cv_id);
            return (
            <div
              key={candidate.cv_id}
              ref={(el) => { cardRefs.current[candidate.cv_id] = el; }}
              onClick={() => handleSelectCV(candidate.cv_id)}
              className={`bg-white rounded-xl p-3 sm:p-3.5 shadow-sm hover:shadow-md border transition-all cursor-pointer group ${
                selectedCV === candidate.cv_id ? 'border-pink-400 bg-pink-50/30' : 'border-slate-200 hover:border-pink-200'
              }`}
            >
              {/* Row 1: Role + badges */}
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="min-w-0 flex-1">
                  {candidate.current_role && (
                    <p className="text-sm lg:text-[15px] font-semibold text-slate-800 leading-snug truncate">
                      {candidate.current_role}
                    </p>
                  )}
                  {candidate.current_level && (
                    <p className="text-[11px] lg:text-xs text-slate-400 mt-0.5">{candidate.current_level}</p>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {showInterestedBadge && (
                    <span className="text-[11px] lg:text-xs font-medium text-pink-500 bg-pink-50 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                      <Heart className="w-2.5 h-2.5 fill-pink-500" />
                    </span>
                  )}
                  {candidate.reach_out_id && candidate.conversation_id && (
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/recruiter-inbox?conversation=${candidate.conversation_id}`); }}
                      className="text-[11px] lg:text-xs font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 px-1.5 py-0.5 rounded-full flex items-center gap-0.5 transition-colors"
                    >
                      <MessageSquare className="w-2.5 h-2.5" />
                    </button>
                  )}
                  <button
                    onClick={(e) => toggleSaveCandidate(candidate.cv_id, e)}
                    className={`p-0.5 rounded-full transition-all touch-manipulation ${
                      savedCvIds.has(candidate.cv_id) ? 'text-slate-700' : 'text-slate-300 hover:text-slate-500'
                    }`}
                  >
                    <Bookmark className={`w-3.5 h-3.5 ${savedCvIds.has(candidate.cv_id) ? 'fill-slate-700' : ''}`} />
                  </button>
                  {candidate.cv_language && (
                    <span className="text-[11px] lg:text-xs text-slate-400 uppercase font-medium">{candidate.cv_language}</span>
                  )}
                </div>
              </div>

              {/* Row 2: Location flow - base → preferred */}
              {(candidate.base_locations || candidate.preferred_locations) && (
                <div className="flex items-center flex-wrap gap-x-1 gap-y-0.5 text-[11px] lg:text-xs mb-1.5">
                  {candidate.base_locations && (
                    <span className="text-slate-500 flex items-center gap-0.5">
                      <MapPin className="w-3 h-3 text-slate-400 shrink-0" />{candidate.base_locations}
                    </span>
                  )}
                  {candidate.base_locations && candidate.preferred_locations && (
                    <ArrowRight className="w-3 h-3 text-pink-300 shrink-0" />
                  )}
                  {candidate.preferred_locations && (
                    <span className="text-slate-500 flex items-center gap-0.5">
                      <Globe className="w-3 h-3 text-slate-400 shrink-0" />{candidate.preferred_locations}
                    </span>
                  )}
                </div>
              )}

              {/* Row 3: Seeking role */}
              {candidate.role_preferences && (
                <div className="flex items-start gap-1 text-[11px] lg:text-xs">
                  <Heart className="w-3 h-3 text-pink-400 shrink-0 mt-0.5" />
                  <p className="text-slate-500 leading-snug line-clamp-2">{candidate.role_preferences}</p>
                </div>
              )}

              {/* Row 4: AI Summary & Fit */}
              {aiResults[candidate.cv_id] ? (
                <div className="mt-2 space-y-1.5 text-[11px] lg:text-xs leading-snug">
                  <div className="bg-amber-50 rounded-lg px-2.5 py-2 border border-amber-200/60">
                    <p className="font-semibold text-amber-700 mb-1 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Summary
                    </p>
                    <ul className="space-y-0.5">
                      {aiResults[candidate.cv_id].summary.split('\n').filter(Boolean).map((b, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-slate-600">
                          <span className="text-amber-400 mt-0.5 text-[7px] shrink-0">●</span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-amber-50/60 rounded-lg px-2.5 py-2 border border-amber-200/40">
                    <p className="font-semibold text-amber-700 mb-1">Fit</p>
                    <ul className="space-y-0.5">
                      {aiResults[candidate.cv_id].fit.split('\n').filter(Boolean).map((b, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-slate-600">
                          <span className="text-amber-400 mt-0.5 text-[7px] shrink-0">●</span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <button
                  onClick={(e) => generateAiSummary(candidate.cv_id, e)}
                  disabled={aiLoading[candidate.cv_id]}
                  className="mt-1.5 flex items-center gap-1 text-[11px] lg:text-xs font-medium text-amber-600 hover:text-amber-700 bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded-md border border-amber-200/60 transition-colors disabled:opacity-50"
                >
                  {aiLoading[candidate.cv_id] ? (
                    <><Loader2 className="w-3 h-3 animate-spin" /> Generating...</>
                  ) : (
                    <><Sparkles className="w-3 h-3" /> Summary &amp; Fit</>
                  )}
                </button>
              )}
            </div>
            );
          })}
          </div>
        );
      })()}

      {/* Infinite scroll sentinel */}
      <div ref={loadMoreRef} className="py-4 flex justify-center">
        {loadingMore && <Loader2 className="w-5 h-5 animate-spin text-pink-400" />}
          </div>
        </div>

        {/* Drag handle */}
        <div
          onMouseDown={onDragStart}
          className="hidden lg:flex items-center justify-center w-2 cursor-col-resize group hover:bg-pink-50 transition-colors flex-shrink-0 relative"
        >
          <div className="w-px h-full bg-slate-200 group-hover:bg-pink-300 transition-colors" />
          <div className="absolute inset-y-0 flex items-center">
            <GripVertical className="w-3 h-3 text-slate-300 group-hover:text-pink-400 transition-colors" />
          </div>
        </div>

        {/* Right Panel: Continuous CV Scroll Feed */}
        <div
          ref={rightPanelRef}
          className={`w-full lg:overflow-y-auto lg:pl-4 lg:h-full flex-1 min-w-0 ${
            selectedCV ? '' : 'hidden lg:flex lg:items-center lg:justify-center'
          }`}
        >
          {/* Mobile back button */}
          {selectedCV && (
            <div className="lg:hidden sticky top-0 bg-white/95 backdrop-blur-sm z-10 border-b border-slate-100 flex items-center justify-between px-2 py-2">
              <button
                onClick={() => { userDismissedDetail.current = true; setSelectedCV(null); }}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-pink-500 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" /> {t('detail.back_to_feed')}
              </button>
              {currentIdx >= 0 && (
                <span className="text-[11px] lg:text-xs text-slate-400 tabular-nums">{currentIdx + 1}/{filteredNav.length}</span>
              )}
            </div>
          )}

          {/* Profile Incomplete Banner */}
          {isProfileIncomplete && selectedCV && (
            <div className="mx-2 mt-2 mb-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
              <div className="mb-1 flex items-center justify-center">
                <AlertCircle className="mr-1.5 size-4 text-slate-600" />
                <span className="text-sm font-semibold text-slate-900">{t('profile_incomplete.title')}</span>
              </div>
              <p className="mb-2 text-xs text-slate-600">{t('profile_incomplete.description')}</p>
              <button type="button" onClick={() => navigate('/recruiter-profile')} className="rounded-lg bg-slate-900 px-4 py-1.5 text-xs font-medium text-white transition-colors hover:bg-slate-800">
                {t('profile_incomplete.button')}
              </button>
            </div>
          )}

          {filteredNav.length > 0 && selectedCV ? (
            <div className="divide-y divide-slate-200">
              {filteredNav.slice(0, displayCount).map((candidate) => {
                const isActive = selectedCV === candidate.cv_id;
                return (
                  <div
                    key={candidate.cv_id}
                    ref={(el) => { cvSectionRefs.current[candidate.cv_id] = el; }}
                    data-cv-id={candidate.cv_id}
                    className="py-4 first:pt-2 px-1 lg:px-0"
                  >
                    {/* Candidate Meta + Actions - compact header */}
                    <div className={`rounded-xl border p-2 sm:p-3 mb-3 transition-colors ${isActive ? 'border-pink-200 bg-pink-50/30' : 'border-slate-200 bg-white'}`}>
                      {/* Top row: save + badges left, contact right */}
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center flex-wrap gap-1">
                          <button
                            onClick={(e) => toggleSaveCandidate(candidate.cv_id, e)}
                            className={`-ml-1 p-1 rounded-full transition-all touch-manipulation ${
                              savedCvIds.has(candidate.cv_id) ? 'text-slate-700' : 'text-slate-300 hover:text-slate-500'
                            }`}
                          >
                            <Bookmark className={`w-3.5 h-3.5 ${savedCvIds.has(candidate.cv_id) ? 'fill-slate-700' : ''}`} />
                          </button>
                          {(!!candidate.is_interested || interestedCvIds.has(candidate.cv_id)) && (
                            <span className="text-xs lg:text-sm font-medium text-pink-500 bg-pink-50 px-1.5 sm:px-2 py-0.5 rounded-full flex items-center gap-0.5">
                              <Heart className="w-2.5 h-2.5 fill-pink-500" /> {t('card.interested')}
                            </span>
                          )}
                          {candidate.cv_language && (
                            <span className="text-xs lg:text-sm text-slate-400 uppercase">{candidate.cv_language}</span>
                          )}
                        </div>
                        {candidate.reach_out_id && candidate.conversation_id ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/recruiter-inbox?conversation=${candidate.conversation_id}`)}
                            className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-emerald-600 transition-all hover:bg-emerald-50 lg:text-xs"
                          >
                            <MessageSquare className="size-3.5" /> {t('detail.go_to_chat')}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={async () => {
                              reachOutCvId.current = candidate.cv_id;
                              if (isProfileIncomplete) { navigate('/recruiter-profile'); return; }
                              setShowReachOut(true);
                              try {
                                const r = await apiCall(`${API_BASE}/api/v1/companies/my/reach-out-suggestions`);
                                if (r.ok) {
                                  const d = await r.json();
                                  if (d.reach_out_suggestions) dedupeAndSetSuggestions(d.reach_out_suggestions);
                                }
                              } catch {}
                            }}
                            className={`flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition-all lg:text-xs ${
                              isProfileIncomplete
                                ? 'cursor-not-allowed bg-slate-100 text-slate-400'
                                : 'text-pink-600 hover:bg-pink-50'
                            }`}
                          >
                            <Send className="size-3.5" /> {t('detail.contact_candidate')}
                          </button>
                        )}
                        {companyId && candidate.explorer_id && (
                          <AtsPushButton
                            companyId={companyId}
                            candidateUserId={candidate.explorer_id}
                            cvId={candidate.cv_id}
                            t={t as any}
                            appearance="outline"
                            hideIcons
                          />
                        )}
                      </div>

                      {/* Current: role + level */}
                      {(candidate.current_role || candidate.current_level) && (
                        <div className="mb-1">
                          {candidate.current_role && (
                            <p className="text-sm font-semibold text-slate-800 leading-snug">{candidate.current_role}</p>
                          )}
                          {candidate.current_level && (
                            <span className="text-xs text-slate-400">{candidate.current_level}</span>
                          )}
                        </div>
                      )}

                      {/* Location flow: base → preferred */}
                      {(candidate.base_locations || candidate.preferred_locations) && (
                        <div className="flex items-center flex-wrap gap-x-1.5 gap-y-0.5 text-xs mb-1.5">
                          {candidate.base_locations && (
                            <span className="text-slate-500 flex items-center gap-0.5">
                              <MapPin className="w-2.5 h-2.5 text-slate-400 shrink-0" />{candidate.base_locations}
                            </span>
                          )}
                          {candidate.base_locations && candidate.preferred_locations && (
                            <ArrowRight className="w-3 h-3 text-pink-300 shrink-0" />
                          )}
                          {candidate.preferred_locations && (
                            <span className="text-slate-500 flex items-center gap-0.5">
                              <Globe className="w-2.5 h-2.5 text-slate-400 shrink-0" />{candidate.preferred_locations}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Seeking: role preferences */}
                      {candidate.role_preferences && (
                        <div className="flex items-start gap-1.5">
                          <Heart className="w-3.5 h-3.5 text-pink-400 shrink-0 mt-0.5" />
                          <p className="text-xs text-slate-600 font-medium leading-snug">{candidate.role_preferences}</p>
                        </div>
                      )}
                    </div>

                    {cvImages[candidate.cv_id] ? (
                      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                        <PassportViewer
                          cvId={candidate.cv_id}
                          audience="recruiter"
                          candidateId={candidate.explorer_id}
                          recruiterPrefs={{
                            career_status: candidate.career_status,
                            base_locations: candidate.base_locations,
                            preferred_locations: candidate.preferred_locations,
                            role_preferences: candidate.role_preferences,
                            current_role: candidate.current_role,
                            current_level: candidate.current_level,
                          }}
                          imagesOverride={cvImages[candidate.cv_id]?.images}
                          fillHeight={false}
                        />
                      </div>
                    ) : (
                      <SkeletonCV />
                    )}
                  </div>
                );
              })}

              {displayCount < filteredNav.length && (
                <div ref={rightPanelSentinelRef} className="py-4 flex justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-pink-400" />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-20">
              <Heart className="w-7 h-7 text-slate-200 mx-auto mb-2" />
              <p className="text-sm text-slate-400">{t('detail.select_hint', { defaultValue: 'Select a candidate to view their CV' })}</p>
            </div>
          )}
        </div>

        {/* Reach Out Modal */}
        <AnimatePresence>
          {showReachOut && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4"
              onClick={() => setShowReachOut(false)}
            >
              <motion.div
                initial={{ opacity: 0, y: 100 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 100 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="max-h-[90vh] w-full overflow-y-auto rounded-t-2xl bg-white shadow-2xl sm:max-w-lg sm:rounded-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-center pt-2 pb-0 sm:hidden">
                  <div className="w-10 h-1 bg-slate-300 rounded-full" />
                </div>

                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 sm:p-5">
                  <div className="flex items-center gap-2">
                    <div className="flex size-8 items-center justify-center rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 shadow-sm">
                      <MessageSquare className="size-4 text-white" />
                    </div>
                    <h2 className="text-sm font-semibold tracking-tight text-slate-900 sm:text-lg">{t('reach_out.title')}</h2>
                  </div>
                  <button type="button" onClick={() => setShowReachOut(false)} className="rounded-lg p-1.5 transition-colors hover:bg-slate-100">
                    <X className="size-5 text-slate-400" />
                  </button>
                </div>

                <form onSubmit={submitReachOut} className="px-4 py-3 sm:p-5 space-y-3 sm:space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('reach_out.job_title')} <span className="text-red-500">*</span></label>
                    {reachOutSuggestions?.job_titles && reachOutSuggestions.job_titles.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {reachOutSuggestions.job_titles.map((jt, idx) => {
                          const label = jt.location ? `${jt.title} (${jt.location})` : jt.title;
                          const isSelected = reachOutForm.job_title === label;
                          return (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => {
                                setReachOutForm((prev) => {
                                  const newTitle = prev.job_title === label ? '' : label;
                                  const message = isTemplateMessageRef.current
                                    ? introTemplateRef.current.replace(/\{\{job_title\}\}/g, newTitle || '{{job_title}}')
                                    : prev.message;
                                  return { ...prev, job_title: newTitle, message };
                                });
                              }}
                              className={`text-[11px] sm:text-xs px-2.5 py-1 rounded-full border transition-all ${
                                isSelected
                                  ? 'bg-pink-500 text-white border-pink-500 shadow-sm'
                                  : 'bg-white text-slate-600 border-slate-200 hover:border-pink-300 hover:text-pink-600'
                              }`}
                            >
                              <span className="flex items-center gap-1">
                                <Briefcase className="w-3 h-3 flex-shrink-0" />
                                {jt.title}
                                {jt.location && <span className="text-[10px] opacity-70">· {jt.location}</span>}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <input
                      value={reachOutForm.job_title}
                      onChange={(e) => {
                        const val = e.target.value;
                        setReachOutForm((prev) => {
                          const message = isTemplateMessageRef.current
                            ? introTemplateRef.current.replace(/\{\{job_title\}\}/g, val || '{{job_title}}')
                            : prev.message;
                          return { ...prev, job_title: val, message };
                        });
                      }}
                      className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 text-sm"
                      placeholder={t('reach_out.job_title_placeholder')}
                      maxLength={300}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('reach_out.salary_range')} <span className="text-red-500">*</span></label>
                    <input
                      value={reachOutForm.salary_range}
                      onChange={(e) => setReachOutForm((f) => ({ ...f, salary_range: e.target.value }))}
                      className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 text-sm"
                      placeholder={t('reach_out.salary_placeholder')}
                      maxLength={100}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t('reach_out.message')} <span className="text-red-500">*</span>
                      {reachOutSuggestions?.intro && !reachOutForm.message && (
                        <button
                          type="button"
                          onClick={() => {
                            const introLang = i18n.language?.startsWith('de') ? 'de' : 'en';
                            const rawIntro = reachOutSuggestions?.intro?.[introLang] || reachOutSuggestions?.intro?.en || '';
                            if (rawIntro) {
                              introTemplateRef.current = rawIntro;
                              isTemplateMessageRef.current = true;
                              const intro = rawIntro.replace(/\{\{job_title\}\}/g, reachOutForm.job_title || '{{job_title}}');
                              setReachOutForm((f) => ({ ...f, message: intro }));
                            }
                          }}
                          className="ml-2 text-[10px] text-pink-500 hover:text-pink-600 font-medium"
                        >
                          {t('reach_out.use_suggestion')}
                        </button>
                      )}
                    </label>
                    <textarea
                      value={reachOutForm.message}
                      onChange={(e) => { isTemplateMessageRef.current = false; setReachOutForm((f) => ({ ...f, message: e.target.value })); }}
                      rows={4}
                      className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 text-sm resize-none"
                      placeholder={t('reach_out.message_placeholder')}
                      maxLength={3000}
                    />
                    <p className={`text-[10px] mt-0.5 font-medium ${reachOutForm.message.length > 2700 ? 'text-red-600' : reachOutForm.message.length > 2400 ? 'text-yellow-600' : 'text-gray-400'}`}>
                      {reachOutForm.message.length}/3000
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('reach_out.nice_to_know')} <span className="text-gray-400 font-normal">({t('reach_out.optional')})</span></label>
                    <textarea
                      value={reachOutForm.nice_to_know}
                      onChange={(e) => setReachOutForm((f) => ({ ...f, nice_to_know: e.target.value }))}
                      rows={2}
                      className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 text-sm resize-none"
                      placeholder={t('reach_out.nice_to_know_placeholder')}
                      maxLength={500}
                    />
                  </div>
                </form>
                <div className="px-4 py-3 sm:p-4 border-t border-gray-100">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowReachOut(false)}
                      disabled={reachOutSubmitting}
                      className="px-3 py-2 text-xs sm:text-sm text-gray-600 font-medium rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                    >
                      {t('reach_out.cancel', { defaultValue: 'Cancel' })}
                    </button>
                    <button
                      type="button"
                      onClick={submitReachOut}
                      disabled={reachOutSubmitting}
                      className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-xs font-medium text-white shadow-sm transition-all hover:bg-slate-800 disabled:opacity-50 sm:text-sm"
                    >
                      {reachOutSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      {reachOutSubmitting ? t('reach_out.sending') : t('reach_out.send')}
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </RecruiterPageShell>
  );
};

export default InterestedFeed;
